import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  LoadProfileImageBody,
  LoadProfileImageData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface LoadProfileImageState
  extends MergeRequestState<LoadProfileImageData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.loadProfileImage,
  isFetching: (state: ReduxState) => state.server.loadProfileImage.isFetching,
  data: (state: ReduxState) => state.server.loadProfileImage.data,
  error: (state: ReduxState) => state.server.loadProfileImage.error,
};

const initialState: LoadProfileImageState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/loadProfileImage';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: LoadProfileImageBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.loadProfileImage(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerLoadProfileImage {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const loadProfileImage: ServerLoadProfileImage = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
